/**
 * Pure reducers for one turn's tool activity timeline.
 *
 * Every function returns the SAME array when nothing changed, so the stores'
 * functional updates never produce a new reference (and a re-render) for a
 * no-op event.
 */

import type {
  ActivityKind,
  ToolCompleteEvent,
  ToolErrorEvent,
  ToolSource,
  ToolStartEvent,
} from '../websocketTypes';

export type ActivityStatus = 'running' | 'done' | 'error' | 'stopped';

export interface ActivityStep {
  /** `tool_call_id`, or `${toolName}#${index}` from a server that sent none. */
  id: string;
  toolName: string;
  label: string;
  doneLabel: string;
  kind: ActivityKind;
  detail?: string | undefined;
  status: ActivityStatus;
  /** Epoch ms: the client clock while live, the server's stored turn start once reloaded. */
  startedAt: number;
  durationMs?: number | undefined;
  resultSummary?: string | undefined;
  sources?: ToolSource[] | undefined;
  error?: string | undefined;
}

/** Append a running step for a `tool_start`. A repeated id is ignored. */
export function startStep(steps: ActivityStep[], ev: ToolStartEvent, now: number): ActivityStep[] {
  const id = ev.tool_call_id ?? `${ev.tool_name}#${steps.length}`;
  if (steps.some(s => s.id === id)) return steps;
  const label = ev.label ?? `Using ${ev.tool_name.replace(/_/g, ' ')}`;
  return [
    ...steps,
    {
      id,
      toolName: ev.tool_name,
      label,
      doneLabel: ev.done_label ?? label,
      kind: ev.kind ?? 'step',
      detail: ev.detail,
      status: 'running',
      startedAt: now,
    },
  ];
}

/** The step a completion or error belongs to: by id, else the oldest running step of that tool. */
function findTarget(steps: ActivityStep[], toolCallId: string | undefined, toolName: string): number {
  if (toolCallId) return steps.findIndex(s => s.id === toolCallId);
  return steps.findIndex(s => s.toolName === toolName && s.status === 'running');
}

/**
 * The newest finished step of a tool. Without an id, Agno's completion already
 * moved a raising call off `running` before its error arrives.
 */
function newestDoneIndex(steps: ActivityStep[], toolName: string): number {
  for (let i = steps.length - 1; i >= 0; i -= 1) {
    const step = steps[i];
    if (step?.toolName === toolName && step.status === 'done') return i;
  }
  return -1;
}

/** Return a copy of `steps` with `patch` merged into the step at `index`. */
function patchStep(steps: ActivityStep[], index: number, patch: Partial<ActivityStep>): ActivityStep[] {
  const current = steps[index];
  if (!current) return steps;
  const next = steps.slice();
  next[index] = { ...current, ...patch };
  return next;
}

/** Mark a step done. An error that already landed for the call wins. */
export function completeStep(steps: ActivityStep[], ev: ToolCompleteEvent): ActivityStep[] {
  const index = findTarget(steps, ev.tool_call_id, ev.tool_name);
  if (index < 0 || steps[index]?.status === 'error') return steps;
  return patchStep(steps, index, {
    status: 'done',
    durationMs: ev.duration_ms,
    resultSummary: ev.result_summary,
    sources: ev.sources,
  });
}

/**
 * Mark a step failed. Overrides `done`: for a raising tool Agno emits
 * ToolCallCompleted and THEN ToolCallError for the same call.
 */
export function failStep(steps: ActivityStep[], ev: ToolErrorEvent): ActivityStep[] {
  let index = findTarget(steps, ev.tool_call_id, ev.tool_name);
  if (index < 0 && !ev.tool_call_id) index = newestDoneIndex(steps, ev.tool_name);
  if (index < 0) return steps;
  return patchStep(steps, index, {
    status: 'error',
    error: ev.error ?? 'Failed',
    durationMs: ev.duration_ms ?? steps[index]?.durationMs,
  });
}

/** End of turn: every still-running step becomes `stopped`, never a success. */
export function settleActivity(steps: ActivityStep[]): ActivityStep[] {
  if (!steps.some(s => s.status === 'running')) return steps;
  return steps.map(s => (s.status === 'running' ? { ...s, status: 'stopped' } : s));
}

/** One step as the server stores it (snake_case; see the phase 2a spec §3). */
export interface StoredActivityStep {
  id: string;
  tool: string;
  label: string;
  done_label: string;
  kind: ActivityKind;
  status: ActivityStatus;
  detail?: string;
  duration_ms?: number;
  result_summary?: string;
  sources?: ToolSource[];
  error?: string;
}

/**
 * Rebuild a reloaded turn's steps. A stored step is always settled: a turn
 * that ended cannot have a call in flight, so anything claiming to still be
 * running is shown stopped rather than spinning forever.
 *
 * @param stored - The raw steps as persisted on the message's metadata.
 * @param startedAt - The turn's stored start time (server clock, epoch ms) applied to every step.
 * @returns The steps in the shape `ActivityTimeline` renders.
 */
export function stepsFromStored(stored: StoredActivityStep[], startedAt: number): ActivityStep[] {
  return stored
    // Server-validated, filtered again here: this array crosses an HTTP
    // boundary, and one bad row must not throw inside the render.
    .filter(row => Boolean(row?.id) && typeof row?.label === 'string')
    .map(row => ({
      id: row.id,
      toolName: row.tool,
      label: row.label,
      doneLabel: row.done_label ?? row.label,
      kind: row.kind ?? 'step',
      status: row.status === 'running' ? 'stopped' : (row.status ?? 'stopped'),
      detail: row.detail,
      startedAt,
      durationMs: row.duration_ms,
      resultSummary: row.result_summary,
      sources: Array.isArray(row.sources) ? row.sources : undefined,
      error: row.error,
    }));
}

/**
 * Apply `fn` to one message's activity inside a message list. Shared by the
 * global slice and the local store so both update identically. Returns the
 * same list when `fn` changed nothing.
 */
export function applyActivityUpdate<M extends { id: string; activity?: ActivityStep[] | undefined }>(
  messages: M[],
  id: string,
  fn: (steps: ActivityStep[]) => ActivityStep[]
): M[] {
  let changed = false;
  const next = messages.map(msg => {
    if (msg.id !== id) return msg;
    const current = msg.activity ?? [];
    const updated = fn(current);
    if (updated === current) return msg;
    changed = true;
    return { ...msg, activity: updated };
  });
  return changed ? next : messages;
}
